import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";

import { Button } from "@covertree/ui";
import { formatPremiumDelta } from "@utils/formatPremiumDelta";

interface EndorsementLayoutFooterProps {
  hidePrice?: boolean;
  premiumDelta: number | null;
  /**
   * Whether the backend could not price the last save/reprice. `premiumDelta`
   * then holds the last figure that *was* priced, so the label is marked stale
   * rather than presented as current.
   */
  isPriceUnavailable?: boolean;
  isRecalculating: boolean;
  isEditMode: boolean;
  /** Whether a reprice is worthwhile — the button is disabled when false. */
  canRecalculate: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  nextLabel: string;
  disableNext?: boolean;
  isSubmitting?: boolean;
  onRecalculate: () => void;
  onBack: () => void;
  onNext: () => void;
}

export function EndorsementLayoutFooter({
  hidePrice = false,
  premiumDelta,
  isPriceUnavailable = false,
  isRecalculating,
  isEditMode,
  canRecalculate,
  isFirstStep,
  isLastStep,
  nextLabel,
  disableNext = false,
  isSubmitting = false,
  onRecalculate,
  onBack,
  onNext,
}: EndorsementLayoutFooterProps) {
  const theme = useTheme();
  const delta = formatPremiumDelta(premiumDelta);

  return (
    <>
      <Divider />
      <Box
        px={3}
        py={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        fontSize={16}
        bgcolor={theme.palette.background.paper}>
        <Box visibility={hidePrice ? "hidden" : "visible"}>
          <Typography
            fontSize="inherit"
            variant="caption"
            color="text.secondary"
            display="block">
            {isPriceUnavailable
              ? "Premium Change (Prorated) — not calculated"
              : "Premium Change (Prorated)"}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {isRecalculating ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <Typography
                  variant="h4"
                  sx={{
                    color: isPriceUnavailable ? "text.disabled" : delta.color,
                    fontWeight: 500,
                  }}>
                  {isPriceUnavailable ? "—" : delta.text}
                </Typography>
                {delta.direction !== "none" && (
                  <Typography
                    fontSize="inherit"
                    variant="caption"
                    color="text.secondary">
                    excl. tax
                  </Typography>
                )}
                {isEditMode && (
                  <Box
                    component="button"
                    onClick={onRecalculate}
                    disabled={!canRecalculate}
                    sx={{
                      ml: 1,
                      px: 1.5,
                      py: 0.5,
                      border: "none",
                      borderRadius: 1,
                      bgcolor: "#e8f5e9",
                      color: "success.dark",
                      fontWeight: 600,
                      fontSize: "inherit",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      "&:hover": {
                        bgcolor: "success.main",
                        color: "white",
                      },
                      "&:disabled": {
                        bgcolor: "action.disabledBackground",
                        color: "action.disabled",
                        cursor: "not-allowed",
                      },
                    }}>
                    <RefreshIcon />
                    Check price
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
        <Stack direction="row" gap={2}>
          {!isFirstStep && (
            <Button
              variant="outlined"
              onClick={onBack}
              disabled={isRecalculating || isSubmitting}
              startIcon={<ArrowBackIcon />}>
              Back
            </Button>
          )}
          <Button
            onClick={onNext}
            loading={isSubmitting}
            disabled={isRecalculating || disableNext || isSubmitting}
            endIcon={isLastStep ? <CheckIcon /> : <ArrowForwardIcon />}>
            {nextLabel}
          </Button>
        </Stack>
      </Box>
    </>
  );
}
